import { supabase } from './supabaseClient';

/**
 * Compress image to WebP format
 * @param {File} file - Image file to compress
 * @param {number} maxWidth - Maximum width in pixels (default 800)
 * @param {number} quality - WebP quality 0-1 (default 0.8)
 * @returns {Promise<Blob>} Compressed image blob
 */
export const compressImage = (file, maxWidth = 800, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onerror = () => reject(new Error('Failed to load image'));
      
      img.onload = () => {
        try {
          // Create canvas
          const canvas = document.createElement('canvas');
          
          // Calculate dimensions
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw image on canvas
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to WebP blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to create blob'));
              }
            },
            'image/webp',
            quality
          );
        } catch (error) {
          reject(error);
        }
      };
      
      img.src = e.target.result;
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Upload image to Supabase Storage
 * @param {File|Blob} file - Image file or blob to upload
 * @param {string} packetId - Packet ID for organizing files
 * @param {number} questionNumber - Question number
 * @returns {Promise<string>} Public URL of uploaded image
 */
export const uploadImage = async (file, packetId = 'temp', questionNumber = 0) => {
  try {
    // Compress image first
    console.log('Compressing image...');
    const compressedBlob = await compressImage(file);
    
    console.log(`Original size: ${(file.size / 1024).toFixed(2)}KB`);
    console.log(`Compressed size: ${(compressedBlob.size / 1024).toFixed(2)}KB`);
    
    // Generate unique filename
    const timestamp = Date.now();
    const filename = `${packetId}_q${questionNumber}_${timestamp}.webp`;
    
    // Upload to Supabase Storage
    console.log('Uploading to Supabase Storage...');
    const { data, error } = await supabase.storage
      .from('question-images')
      .upload(filename, compressedBlob, {
        contentType: 'image/webp',
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) {
      console.error('Upload error:', error);
      throw error;
    }
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from('question-images')
      .getPublicUrl(filename);
    
    console.log('Upload successful!', urlData.publicUrl);
    return urlData.publicUrl;
    
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

/**
 * Delete image from Supabase Storage
 * @param {string} imageUrl - Public URL of image to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteImage = async (imageUrl) => {
  try {
    // Extract filename from URL
    // URL format: https://xxx.supabase.co/storage/v1/object/public/question-images/filename.webp
    const urlParts = imageUrl.split('/');
    const filename = urlParts[urlParts.length - 1];
    
    const { error } = await supabase.storage
      .from('question-images')
      .remove([filename]);
    
    if (error) {
      console.error('Delete error:', error);
      throw error;
    }
    
    console.log('Image deleted:', filename);
    return true;
    
  } catch (error) {
    console.error('Error deleting image:', error);
    return false;
  }
};

/**
 * Get image size info
 * @param {File} file - Image file
 * @returns {Promise<Object>} Image dimensions and size
 */
export const getImageInfo = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          size: file.size,
          sizeKB: (file.size / 1024).toFixed(2),
          type: file.type
        });
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

export default {
  compressImage,
  uploadImage,
  deleteImage,
  getImageInfo
};